import React, { useState, useEffect, useMemo } from 'react';
import { Search, Megaphone, Download, RefreshCw, Send, Trash2, Users, CheckCircle, AlertCircle } from 'lucide-react';

const AdminNewsletterTab = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [subscriberSearch, setSubscriberSearch] = useState('');
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastData, setBroadcastData] = useState({ subject: '', message: '' });
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const authHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  const fetchSubscribers = async () => {
    try {
      setSubscribersLoading(true);
      const response = await fetch(`${API_URL}/api/admin/newsletter/subscribers`, { headers: authHeaders() });
      const data = await response.json();
      setSubscribers(data.subscribers || []);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    } finally {
      setSubscribersLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const filteredSubscribers = useMemo(() =>
    subscribers.filter(s =>
      s.email.toLowerCase().includes(subscriberSearch.toLowerCase())
    ),
    [subscribers, subscriberSearch]
  );

  const handleDeleteSubscriber = async (email) => {
    if (!window.confirm(`Moechten Sie ${email} wirklich abmelden?`)) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter/subscribers/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (response.ok) {
        setSubscribers(prev => prev.filter(s => s.email !== email));
      }
    } catch (error) {
      console.error('Error deleting subscriber:', error);
    }
  };

  const handleExportCSV = () => {
    const csv = ['E-Mail,Anmeldedatum'];
    subscribers.forEach(s => {
      const date = s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString('de-DE') : '';
      csv.push(`${s.email},${date}`);
    });
    const blob = new Blob([csv.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-abonnenten-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSendBroadcast = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Newsletter an ${subscribers.length} Abonnenten senden?`)) return;
    setBroadcastSending(true);
    setBroadcastResult(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter/send`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(broadcastData)
      });
      const data = await response.json();
      if (response.ok) {
        setBroadcastResult({ type: 'success', text: data.message, details: data.details });
        setBroadcastData({ subject: '', message: '' });
      } else {
        setBroadcastResult({ type: 'error', text: data.detail || 'Fehler beim Versand' });
      }
    } catch {
      setBroadcastResult({ type: 'error', text: 'Verbindungsfehler' });
    } finally {
      setBroadcastSending(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
        <h2 className="font-bold text-gray-900" data-testid="newsletter-heading">
          Newsletter-Abonnenten ({filteredSubscribers.length})
        </h2>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="E-Mail suchen..."
              value={subscriberSearch}
              onChange={(e) => setSubscriberSearch(e.target.value)}
              data-testid="newsletter-search"
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-64"
            />
          </div>
          <button
            onClick={() => { setShowBroadcast(!showBroadcast); setBroadcastResult(null); }}
            disabled={subscribers.length === 0}
            data-testid="newsletter-broadcast-btn"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <Megaphone className="w-4 h-4" />
            Newsletter senden
          </button>
          <button
            onClick={handleExportCSV}
            disabled={subscribers.length === 0}
            data-testid="newsletter-export-btn"
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={fetchSubscribers}
            data-testid="refresh-subscribers-btn"
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${subscribersLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {showBroadcast && (
        <div className="p-6 border-b border-gray-200 bg-blue-50/50">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Megaphone className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold text-gray-900">Newsletter an alle Abonnenten senden</h3>
              <span className="text-sm text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">{subscribers.length} Empfaenger</span>
            </div>
            <form onSubmit={handleSendBroadcast} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Betreff</label>
                <input
                  type="text"
                  value={broadcastData.subject}
                  onChange={(e) => setBroadcastData({ ...broadcastData, subject: e.target.value })}
                  required
                  data-testid="broadcast-subject"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. Neuigkeiten aus der Gemeinde"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nachricht</label>
                <textarea
                  value={broadcastData.message}
                  onChange={(e) => setBroadcastData({ ...broadcastData, message: e.target.value })}
                  required
                  rows={6}
                  data-testid="broadcast-message"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Ihre Nachricht an alle Newsletter-Abonnenten..."
                />
              </div>
              {broadcastResult && (
                <div data-testid="broadcast-result" className={`p-4 rounded-lg flex items-start gap-3 ${
                  broadcastResult.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}>
                  {broadcastResult.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                  <div>
                    <p className="font-medium">{broadcastResult.text}</p>
                    {broadcastResult.details && broadcastResult.details.failed > 0 && (
                      <p className="text-sm mt-1">{broadcastResult.details.failed} E-Mail(s) konnten nicht zugestellt werden.</p>
                    )}
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={broadcastSending}
                  data-testid="broadcast-send-btn"
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {broadcastSending ? 'Wird gesendet...' : `An ${subscribers.length} Abonnenten senden`}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowBroadcast(false); setBroadcastResult(null); }}
                  className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {subscribersLoading ? (
        <div className="p-12 text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Lade Abonnenten...</p>
        </div>
      ) : filteredSubscribers.length === 0 ? (
        <div className="p-12 text-center text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>{subscribers.length === 0 ? 'Noch keine Abonnenten vorhanden' : 'Keine Ergebnisse gefunden'}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="newsletter-table">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Nr.</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">E-Mail-Adresse</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Anmeldedatum</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredSubscribers.map((sub, index) => (
                <tr key={sub.email} className="hover:bg-blue-50/50 transition-colors">
                  <td className="px-6 py-4 text-sm text-gray-400">{index + 1}</td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900" data-testid={`subscriber-email-${index}`}>
                      {sub.email}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {sub.subscribed_at
                      ? new Date(sub.subscribed_at).toLocaleDateString('de-DE', {
                          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })
                      : '-'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleDeleteSubscriber(sub.email)}
                      data-testid={`delete-subscriber-${index}`}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Abonnent loeschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminNewsletterTab;
